import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
  Grid,
} from "@mui/material";
import {
  Send,
  MailOpen,
  MousePointerClick,
  UserPlus,
  CreditCard,
  DollarSign,
  Users,
  Ban,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import {
  fetchMarketingDashboard,
  fetchPipeline,
  fetchUpcomingTasks,
} from "../../../../../../redux/actions/marketingAction";
import {
  Building2,
  Calendar,
  Phone,
  Mail as MailIcon,
  RotateCcw,
  Monitor,
  MoreHorizontal,
  ListChecks,
} from "lucide-react";

const MetricCard = ({ icon: Icon, label, value, suffix, color, subValue }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        transition: "all 0.2s",
        "&:hover": {
          borderColor: alpha(color || theme.palette.primary.main, 0.3),
          boxShadow: `0 4px 16px ${alpha(color || theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(color || theme.palette.primary.main, 0.1),
          flexShrink: 0,
        }}
      >
        <Icon size={20} color={color || theme.palette.primary.main} strokeWidth={1.75} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, fontWeight: 500, display: "block", mb: 0.25 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontWeight: 700, fontSize: "1.35rem", color: theme.palette.text.primary, lineHeight: 1.2 }}
        >
          {value}
          {suffix && (
            <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 500, color: theme.palette.text.secondary, ml: 0.5 }}>
              {suffix}
            </Typography>
          )}
        </Typography>
        {subValue && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.25, display: "block" }}>
            {subValue}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const FunnelBar = ({ label, count, percentage, maxPercentage, color }) => {
  const theme = useTheme();
  const width = maxPercentage > 0 && count > 0 ? (percentage / maxPercentage) * 100 : 0;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {count.toLocaleString()} ({percentage.toFixed(1)}%)
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          borderRadius: "4px",
          bgcolor: alpha(color, 0.1),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${width}%`,
            borderRadius: "4px",
            bgcolor: color,
            transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </Box>
    </Box>
  );
};

const MarketingDashboard = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { dashboard, loading, pipeline, upcomingTasks } = useSelector((state) => state.marketing);

  useEffect(() => {
    dispatch(fetchMarketingDashboard());
    dispatch(fetchPipeline({ page_size: 5 }));
    dispatch(fetchUpcomingTasks());
  }, [dispatch]);

  if (loading && !dashboard) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  const d = dashboard || {};
  const funnelColors = [
    theme.palette.info?.main || "#6366F1",
    "#8b5cf6",
    theme.palette.primary.main,
    "#f59e0b",
    "#10b981",
    "#06b6d4",
    "#ec4899",
  ];

  return (
    <Box>
      {/* Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <MetricCard
            icon={Users}
            label={t('mkt_total_contacts')}
            value={(d.total_contacts || 0).toLocaleString()}
            color={theme.palette.info?.main || "#6366F1"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <MetricCard
            icon={Send}
            label={t('mkt_emails_sent')}
            value={(d.total_emails_sent || 0).toLocaleString()}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <MetricCard
            icon={MailOpen}
            label={t('mkt_open_rate')}
            value={`${d.open_rate || 0}%`}
            color="#10b981"
            subValue={`${(d.total_emails_opened || 0).toLocaleString()} ${t('mkt_opened')}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <MetricCard
            icon={MousePointerClick}
            label={t('mkt_click_rate')}
            value={`${d.click_rate || 0}%`}
            color="#8b5cf6"
            subValue={`${(d.total_emails_clicked || 0).toLocaleString()} ${t('mkt_clicked')}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <MetricCard
            icon={AlertTriangle}
            label={t('mkt_bounce_rate')}
            value={`${d.bounce_rate || 0}%`}
            color={theme.palette.error?.main || "#EC4899"}
            subValue={`${(d.total_bounced || 0).toLocaleString()} ${t('mkt_bounced')}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <MetricCard
            icon={UserPlus}
            label={t('mkt_active_trials')}
            value={(d.active_trials || 0).toLocaleString()}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <MetricCard
            icon={CreditCard}
            label={t('mkt_paying_customers')}
            value={(d.paying_customers || 0).toLocaleString()}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <MetricCard
            icon={DollarSign}
            label={t('mkt_mrr')}
            value={`$${(d.mrr || 0).toLocaleString()}`}
            color={theme.palette.primary.main}
          />
        </Grid>
      </Grid>

      {/* Funnel + Info */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "0.95rem", mb: 2.5 }}>
              {t('mkt_overall_funnel')}
            </Typography>
            {(() => {
              const totalSent = (d.total_emails_sent || 0) + (d.total_wa_sent || 0);
              const pct = (val) => totalSent ? (val / totalSent * 100) : 0;
              return (
                <>
                  <FunnelBar label={t('mkt_emails_sent')} count={d.total_emails_sent || 0} percentage={totalSent ? ((d.total_emails_sent || 0) / totalSent * 100) : 0} maxPercentage={100} color={funnelColors[0]} />
                  <FunnelBar label={t('mkt_wa_sent')} count={d.total_wa_sent || 0} percentage={totalSent ? ((d.total_wa_sent || 0) / totalSent * 100) : 0} maxPercentage={100} color={funnelColors[1]} />
                  <FunnelBar label={t('mkt_delivered')} count={(d.total_emails_delivered || 0) + (d.total_wa_delivered || 0)} percentage={pct((d.total_emails_delivered || 0) + (d.total_wa_delivered || 0))} maxPercentage={100} color={funnelColors[2]} />
                  <FunnelBar label={t('mkt_opened')} count={(d.total_emails_opened || 0) + (d.total_wa_read || 0)} percentage={pct((d.total_emails_opened || 0) + (d.total_wa_read || 0))} maxPercentage={100} color={funnelColors[3]} />
                  <FunnelBar label={t('mkt_clicked')} count={(d.total_emails_clicked || 0) + (d.total_wa_replied || 0)} percentage={pct((d.total_emails_clicked || 0) + (d.total_wa_replied || 0))} maxPercentage={100} color={funnelColors[4]} />
                  <FunnelBar label={t('mkt_active_trials')} count={d.active_trials || 0} percentage={pct(d.active_trials || 0)} maxPercentage={100} color={funnelColors[5]} />
                  <FunnelBar label={t('mkt_paying')} count={d.paying_customers || 0} percentage={pct(d.paying_customers || 0)} maxPercentage={100} color={funnelColors[6]} />
                </>
              );
            })()}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              height: "100%",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "0.95rem", mb: 2.5 }}>
              {t('mkt_quick_stats')}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[
                { label: t('mkt_total_campaigns'), value: d.total_campaigns || 0 },
                { label: t('mkt_active_campaigns'), value: d.active_campaigns || 0 },
                { label: t('mkt_unsubscribed'), value: d.total_unsubscribed || 0 },
                { label: t('mkt_contacts'), value: d.total_contacts || 0 },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    {item.value.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Pipeline Summary + Upcoming Tasks */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Pipeline Summary */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: "12px", border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "0.95rem", mb: 2 }}>
              Pipeline
            </Typography>
            {(() => {
              const summary = pipeline?.summary || {};
              const statuses = [
                { key: "lead", label: "Lead", color: "#6b7280" },
                { key: "testing", label: "Testing", color: "#3b82f6" },
                { key: "demo_booked", label: "Demo Booked", color: "#8b5cf6" },
                { key: "proposal", label: "Proposal", color: "#f59e0b" },
                { key: "closed", label: "Closed", color: "#10b981" },
                { key: "lost", label: "Lost", color: "#ef4444" },
              ];
              return (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {statuses.map((s) => {
                    const data = summary[s.key] || { count: 0, total_value: 0 };
                    return (
                      <Box key={s.key} sx={{ flex: 1, minWidth: 100, p: 1.5, borderRadius: "10px", border: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: s.color }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.6rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>{s.label}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "1rem" }}>{data.count}</Typography>
                        {data.total_value > 0 && (
                          <Typography variant="caption" sx={{ color: s.color, fontWeight: 600, fontSize: "0.65rem" }}>
                            {Number(data.total_value).toLocaleString()} MAD
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </Paper>
        </Grid>

        {/* Upcoming Tasks */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <ListChecks size={16} color={theme.palette.text.secondary} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                Tâches du jour
              </Typography>
            </Box>
            {(upcomingTasks || []).length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.palette.text.disabled, textAlign: "center", py: 2 }}>
                Aucune tâche en attente
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {(upcomingTasks || []).slice(0, 8).map((task) => {
                  const taskIcons = { call: Phone, email: MailIcon, follow_up: RotateCcw, demo: Monitor, meeting: Calendar, other: MoreHorizontal };
                  const taskColors = { call: "#3b82f6", email: "#8b5cf6", follow_up: "#f59e0b", demo: "#10b981", meeting: "#6366f1", other: "#6b7280" };
                  const TaskIcon = taskIcons[task.type] || MoreHorizontal;
                  const taskColor = taskColors[task.type] || "#6b7280";
                  return (
                    <Box key={task.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, px: 1, borderRadius: "6px", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                      <TaskIcon size={13} color={taskColor} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }} noWrap>{task.title}</Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.6rem", color: theme.palette.text.disabled }} noWrap>
                          {task.organization_name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: "0.6rem", color: task.overdue ? "#ef4444" : "#f59e0b", fontWeight: 600, flexShrink: 0 }}>
                        {task.overdue ? "En retard" : "Aujourd'hui"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarketingDashboard;
